import {
  collection,
  doc,
  query,
  serverTimestamp,
  type Timestamp,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { buildUnsupportedConverter } from '../shared/noops'
import { type FirestorePromptPostRoomStateAIEvent, schema } from './schema'
import { type PostRoomStatePromptType } from '../PromptPostRoomStateType/types'
import { type FirebaseRepository } from '../../models/FirebaseRepository'
import {
  collectionSnapshots,
  modelListStream,
} from '../../firestore-mobx/stream'
import { PromptPostRoomStateAIEvent } from '../../models/PromptPostRoomStateAIEvent'
import { addDocWithError, getDocWithError } from '../../firestore-mobx/fetch'
import { PromptType } from '../PromptVersion/types'
import { StreamController } from 'tricklejs'

export * from './schema'

const unsupported =
  buildUnsupportedConverter<FirestorePromptPostRoomStateAIEvent>()

const readConverter: FirestoreDataConverter<FirestorePromptPostRoomStateAIEvent> =
  {
    toFirestore: unsupported.toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

const getColRef = (repository: FirebaseRepository) =>
  collection(
    repository.firestore,
    'prompt',
    'post_room_state_prompt',
    'ai'
  ).withConverter(readConverter)

// todo: add compound index for promptType and promptVersionId on this collection
export const getPromptPostRoomStateAIEvents = (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
  }
) => {
  const q = query(
    getColRef(repository),
    where('promptType', '==', promptType),
    where('promptVersionId', '==', promptVersionId)
  )
  return modelListStream(repository, q, PromptPostRoomStateAIEvent)
}

export const startPostRoomStatePromptTestForRoom = async (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
    roomId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
    roomId: string
  }
) => {
  const colRef = getColRef(repository)

  await addDocWithError(
    colRef.withConverter(null),
    {
      endedAt: null,
      promptType,
      promptVersionId,
      roomId,
      startedAt: serverTimestamp(),
    },
    'StartPostRoomStatePromptTestForRoom'
  )

  return true
}

export const getResultsForPostRoomStatePromptTests = (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
  }
) => {
  type Result = QueryDocumentSnapshot<{ updatedAt: Timestamp; roomId: string }>
  type ResultParent = QueryDocumentSnapshot<{ systemPrompt?: string }>

  // collection ref for parent test/{roomId}/results/{testInstanceId}
  const resultsPerRoom = collection(
    repository.firestore,
    'prompt',
    PromptType.postRoomState,
    'type',
    promptType,
    'prompt_version',
    promptVersionId,
    'test'
  )

  // stream yields all results docs and also the parent docs (roomId === docId)
  const controller = new StreamController<{
    results: Result[]
    perRoomTestParents: ResultParent[]
  }>()

  const unsubscribers: (() => void)[] = []

  // map of results doc for each test for a given roomId (as of now these don't get deleted )
  const activeResults: Map<string, Result[]> = new Map()

  let lastPerRoomDocs: QueryDocumentSnapshot[] = []

  // when we update active results, add them to the stream
  const resyncResultsStream = () => {
    const results = [...activeResults.values()].flat()
    controller.add({
      results,
      perRoomTestParents: lastPerRoomDocs,
    })
  }

  // cleanup function to unsubscribe all listeners and reset state
  const unsubscribeAll = () => {
    unsubscribers.forEach((unsub) => unsub())
    unsubscribers.length = 0
    activeResults.clear()
  }

  // when the root collection changes, un-sub old streams and re-sub for each test doc (roomId)
  collectionSnapshots(resultsPerRoom).listen((snapshot) => {
    unsubscribeAll()
    lastPerRoomDocs = snapshot.docs

    snapshot.docs.forEach((doc) => {
      const resultRef = collection(doc.ref, 'result')

      // emit results as they come in
      const unsub = collectionSnapshots(resultRef).listen((snap) => {
        activeResults.set(doc.id, snap.docs as Result[])
        resyncResultsStream()
      })

      // add new stream to unsub
      unsubscribers.push(() => unsub.cancel())
    })

    resyncResultsStream()
  })

  controller.onCancel = () => unsubscribeAll()

  return controller.stream
}

// todo: we may want some schemas to parse the results into, will be unique for each post_room_state prompt type
export const fetchResultsForSuccessfulPostRoomStatePromptTest = async (
  repository: FirebaseRepository,
  {
    promptType,
    promptVersionId,
    roomId,
  }: {
    promptType: PostRoomStatePromptType
    promptVersionId: string
    roomId: string
  }
) => {
  const resultsRef = doc(
    repository.firestore,
    'prompt',
    PromptType.postRoomState,
    'type',
    promptType,
    'prompt_version',
    promptVersionId,
    'test',
    roomId
  )
  const snap = await getDocWithError(
    resultsRef,
    'fetchResultsForSuccessfulPostRoomStatePromptTest'
  )
  if (!snap.exists())
    throw new Error(
      `No results found for prompt type ${promptType}, version ${promptVersionId}, room ${roomId}`
    )
  return snap.data()
}
